import { TaskModel } from '@/app/models/tasks/taskModel'
import { TaskStatus } from '@/app/constants/taskStatus'
import { getImportTaskStatus } from './getImportTaskStatus'

const POLL_INTERVAL_MS = 1000
const POLL_TIMEOUT_MS = 30 * 60_000
// Tolerated in a row before the poll gives up; a single dropped status request
// must not fail a task that keeps running server-side.
const MAX_CONSECUTIVE_POLL_ERRORS = 3

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

export type TaskProgress = { current: number; total: number }

// The status endpoint may return the progress fields in PascalCase depending on
// the serializer (same fallback as the taskReducer).
export const readTaskProgress = (task: TaskModel): TaskProgress | null => {
  const current = task.progressCurrent ?? (task as any).ProgressCurrent ?? 0
  const total = task.progressTotal ?? (task as any).ProgressTotal ?? 0
  return total > 0 ? { current, total } : null
}

/**
 * Polls an import task until it completes. Throws on task Error status, on
 * timeout, and after MAX_CONSECUTIVE_POLL_ERRORS dropped status requests.
 * Returns silently when `isCancelled` reports true (e.g. component unmount) —
 * the task keeps running server-side either way.
 */
export async function waitForImportTask({
  taskId,
  onProgress,
  isCancelled = () => false,
}: {
  taskId: string
  onProgress?: (progress: TaskProgress | null) => void
  isCancelled?: () => boolean
}): Promise<void> {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  let consecutiveErrors = 0

  while (!isCancelled()) {
    if (Date.now() > deadline) {
      throw new Error(`Timeout waiting for task ${taskId}`)
    }

    let task: TaskModel | null = null
    try {
      task = (await getImportTaskStatus({ importTaskId: taskId })).data
      consecutiveErrors = 0
    } catch (pollErr) {
      consecutiveErrors += 1
      if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        throw pollErr
      }
    }

    // Terminal checks stay outside the try — a "task failed" throw must not be
    // swallowed as a dropped poll.
    if (task?.status === TaskStatus.Completed) {
      return
    }
    if (task?.status === TaskStatus.Error) {
      throw new Error(task.message || `Task failed (taskId=${taskId})`)
    }
    if (task) {
      onProgress?.(readTaskProgress(task))
    }

    await sleep(POLL_INTERVAL_MS)
  }
}
